// Handles data loading for full fidelity plots. We also still have "sampled"
// plots that are loaded through the usePanelRunsData hook, but full fidelity
// is now the default and we want to depcrecate the sampled style.
//
// Currently we create one BucketedQueryManager per runset per panel, in SharedPanelStateContext.
// This manager is shared across the regular panel chart view, fullscreen mode, and
// edit mode. We share the manager so that fullscreen and edit modes can immediately
// show data when opened.
//
// This works by:
// - maintaining a cache of of requests, their responses, and active listeners
//   (which are just callback functions)
// - deduping exactly the same requests to existing data
// - if a new request cannot be exactly matched, we find the best existing data
//   to immediately send to the handler, while making the requested query in parallel
// - see findBestExistingData for the matching logic
//
// We should move all of the "heavy data processing" logic related to line plots here
// and out of hooks. That will make it easier to reason about performance, test
// performance, move things to webworkers, etc.
//
// The following are canadidates for moving here:
// - make this handle multiple runsets for Reports
// - move post processing logic (like useLines) here, out of hooks
// - move all zoom logic here, out of hooks

import isEqual from 'lodash/isEqual';
import {v4 as uuidv4} from 'uuid';

import {type RunsDataQueryConfig} from '../../../containers/RunsDataLoader';
import {
  type BucketedHistorySpec,
  specIsStepAxis,
} from '../../../state/runs/types';
import {getIncompleteRetryDelayMs} from '../../../util/apolloRetryAfter';
import {type RequestCause} from '../../../util/apolloRetryHeader';
import {
  recordRumAction,
  recordRumDurationVital,
} from '../../../util/datadog/datadogRum';
import {type Range} from '../common';
import {type SingleQuery} from './../../../util/queryTypes';
import {bucketedQuery} from './bucketedDeltaQuery';
import {IS_POLL_TESTING_ENV} from './imperativePollTrigger';
import {type BucketedQueryType} from './queries/queryVars';
import {type RunsDataById} from './types';
import {DEFAULT_LOGGING_INFO, type LoggingInfo} from './useLoggingInfo';
import {bracketHistoriesData} from './util';

const INCOMPLETE_FETCH_MAX_RETRIES = 3;

/**
 * Progressive timeout delays for BRSDQ queries.
 * Each retry attempt gets more time, enabling fast initial renders while still loading slow runs.
 * - Attempt 0 (initial): 3s - fast timeout, show partial data quickly
 * - Attempt 1 (retry 1): 5s - slightly longer for stragglers
 * - Attempt 2 (retry 2): 15s - more patience for slow runs
 * - Attempt 3 (final): 30s - maximum time before giving up
 */
const TIMEOUT_DELAYS = [3, 5, 15, 30] as const;

/**
 * Timeout used when we detect that retries are making no progress and want to
 * give the underlying service one last, longer window before giving up. We
 * jump directly to this instead of stepping through the normal ladder because
 * a stalled fetch is almost always a slow/sick backend rather than something
 * the intermediate timeouts would help with.
 */
const NO_PROGRESS_ESCALATION_TIMEOUT = 45;

function getTimeoutForAttempt(attempt: number): number {
  return TIMEOUT_DELAYS[Math.min(attempt, TIMEOUT_DELAYS.length - 1)];
}

/**
 * `runsDataQuery` without the panel's runset subqueries. A fetch reads only
 * this runset's `singleQuery` and the panel-wide fields, so an edit to another
 * runset must not change this runset's request.
 */
function withoutRunsetQueries(
  runsDataQuery: RunsDataQueryConfig
): RunsDataQueryConfig {
  const {queries, runsetQueries, ...panel} =
    runsDataQuery as RunsDataQueryConfig & {queries?: unknown};
  return {...panel, runsetQueries: []};
}

/**
 * Returns true if two incomplete run-ID arrays represent the same set.
 * Used to detect no-progress retries (same runs failed both times) so we can
 * short-circuit the normal 3s/5s/15s/30s ladder. The caller then either
 * escalates to a single 45s attempt or ejects with partial data, depending
 * on whether escalation has already happened.
 *
 * O(n) time, O(n) space: builds a single Set and short-circuits on the first
 * membership miss. Assumes run IDs within each array are unique, which the
 * server guarantees.
 */
function sameIncompleteSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const aSet = new Set(a);
  for (const id of b) {
    if (!aSet.has(id)) {
      return false;
    }
  }
  return true;
}

function incompleteRefetchCause(
  isErrorRetry: boolean,
  heldBackForRetryAfter: boolean
): RequestCause {
  if (isErrorRetry) {
    return 'error-refetch';
  }
  return heldBackForRetryAfter
    ? 'incomplete-refetch-delayed'
    : 'incomplete-refetch';
}

/** Wait out a partial-shed Retry-After before the next incomplete-refetch. */
async function delayIncompleteRetry(
  retryAfterSeconds: number | undefined
): Promise<boolean> {
  const ms = getIncompleteRetryDelayMs(retryAfterSeconds);
  if (ms <= 0) {
    return false;
  }
  await new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });
  // A zero-second hint yields jitter only, which is not a hold-back.
  return (retryAfterSeconds ?? 0) > 0;
}

// Observability for incomplete fetch lifecycle
// - initial: first time we detect incomplete runs and start the fetch sequence
// - retry: subsequent attempt after previous fetch still returned incomplete runs
// - error_retry: retry triggered by an error during fetch
// - no_progress_escalate: retry returned the same set of incomplete runs as the
//   previous attempt; instead of stepping through the normal ladder we jump to
//   a single long-timeout attempt
// - no_progress_eject: same-set detected after the long-timeout attempt also
//   failed to make progress, so we exit with whatever partial data we have
interface IncompleteActionContext {
  phase:
    | 'initial'
    | 'retry'
    | 'error_retry'
    | 'no_progress_escalate'
    | 'no_progress_eject';
  traceId: string; // Links all retries for a single incomplete fetch sequence
  attempt: number | undefined;
  incompleteCount: number | undefined;
  queryType: BucketedQueryType;
  timeoutSeconds?: number;
  errorMessage?: string;
}

const logIncompleteAction = (context: IncompleteActionContext) => {
  recordRumAction('BucketedQueryManager: incomplete_fetch', context);
};

type RequestHandler = (state: BucketedQueryState) => void;

// One running queryFn call, and whether a panel is still waiting for it. When
// the call finishes it is reported as the workspace.delta_query_attempt vital.
// Times come from performance.now(). See query-ownership.md.
interface QueryAttempt {
  startTime: number;
  // When the last panel left while this call was still running.
  panelLeftTime?: number;
  // No panel was waiting when this call started.
  startedWithoutPanel: boolean;
}

interface BucketedQueryRequest {
  expressions: string[] | undefined;
  handlers: Array<{
    handler: RequestHandler;
    active: boolean;
  }>;
  nBuckets: number;
  // The queryFn call running for this request, if any. In practice this holds
  // at most one entry: follow-ups wait for the previous call, and poll() skips
  // a request that is still loading.
  runningAttempts: Set<QueryAttempt>;
  queryPromise: Promise<BucketedData> | null;
  queryType: BucketedQueryType;
  runsDataQuery: Exclude<RunsDataQueryConfig, 'queries'>;
  singleQuery: SingleQuery;
  state: BucketedQueryState;
}

interface BucketedData {
  runsById: RunsDataById;
  order: string[]; // Explicit run order from API's delta.order
  isAggregated: boolean;
  incomplete?: string[]; // Run IDs that couldn't be loaded and need follow-up query
  // From Retry-After on a partial-shed 200; delays the next incomplete-refetch.
  retryAfterSeconds?: number;
}

export interface BucketedQueryState {
  data: BucketedData | null;
  error: Error | null;
  lastUpdated: number;
  loading: boolean;
  lowerFidelityData: BucketedData | null;
}

export type BucketedQueryFn = (args: {
  expressions: string[] | undefined;
  nBuckets: number;
  currentRunsById: RunsDataById; // Changed from prevRunsById to match implementation
  runsDataQuery: Exclude<RunsDataQueryConfig, 'queries'>; // Changed from query to match implementation
  queryType: BucketedQueryType;
  singleQuery: SingleQuery;
  loggingInfo: LoggingInfo;
  timeout: number | undefined;
  cause?: RequestCause;
}) => Promise<BucketedData>;

export class BucketedQueryManager {
  private boundPollHandler?: (event: Event) => void;
  private requests = new Array<BucketedQueryRequest>();
  private currentGeneration = 0;

  constructor(
    private runsetId: string,
    private queryFn: BucketedQueryFn = bucketedQuery
  ) {
    /**
     * Attaches a listener to the class under specific conditions. This allows us to emit an event (in this case a signal to force a poll event on each BQM) and have all of the BucketQueryManagers trigger their poll method. This behavior is not intended for prod environments. It's only intended for test environments where we want to trigger polling behavior to verify that the class has unregistered stale requests and isn't thrashing the UI by calling multiple requests per polling loop.
     *
     * NOTE: there is no functionality removing the event listener because we don't have any bulletproof way to detect when these classes are being destroyed. This is why this should only be enabled in a short-lived test environment.
     */
    if (IS_POLL_TESTING_ENV) {
      this.boundPollHandler = this.poll.bind(this);
      window.addEventListener('WANDB_BQM_POLL', this.boundPollHandler);
    }
  }

  private cleanupInactiveRequests() {
    this.requests = this.requests.filter(request =>
      request.handlers.some(h => h.active)
    );
  }

  private startQuery(
    request: BucketedQueryRequest,
    generation: number,
    loggingInfo?: LoggingInfo,
    cause?: RequestCause
  ) {
    request.state = {
      ...request.state,
      loading: true,
      error: null,
    };
    this.notifyHandlers(request);

    const initialTimeout = getTimeoutForAttempt(0);
    // One ID for this query and all of its follow-ups, so their telemetry can
    // be grouped into a chain. Created first so attempt 0 can report it.
    const traceId = uuidv4();

    request.queryPromise = this.fetchData({
      request,
      traceId,
      attemptNumber: 0,
      expressions: request.expressions,
      generation,
      nBuckets: request.nBuckets,
      prevRunsById: request.state.data?.runsById ?? {},
      queryType: request.queryType,
      runsDataQuery: request.runsDataQuery,
      singleQuery: request.singleQuery,
      loggingInfo: loggingInfo ?? DEFAULT_LOGGING_INFO,
      timeout: initialTimeout,
      cause,
    });

    request.queryPromise
      .then(async data => {
        if (this.currentGeneration === generation) {
          const hasIncomplete = data.incomplete && data.incomplete.length > 0;

          request.state = {
            ...request.state,
            data,
            error: null,
            lastUpdated: Date.now(),
            loading: Boolean(hasIncomplete),
          };

          // Notify handlers immediately so UI can render partial data
          this.notifyHandlers(request);

          logIncompleteAction({
            phase: 'initial',
            traceId,
            incompleteCount: data.incomplete?.length ?? 0,
            attempt: 0,
            queryType: request.queryType,
            timeoutSeconds: initialTimeout,
          });

          // If there are incomplete runs, fetch them in a follow-up query.
          // Honor Retry-After from a partial shed before hammering again.
          if (hasIncomplete) {
            const heldBack = await delayIncompleteRetry(data.retryAfterSeconds);
            if (this.currentGeneration !== generation) {
              return data;
            }
            this.fetchIncompleteRuns({
              request,
              generation,
              loggingInfo,
              attempt: 1,
              traceId,
              isErrorRetry: false,
              previousIncomplete: data.incomplete ?? [],
              heldBackForRetryAfter: heldBack,
            });
          }
        }
        return data;
      })
      .catch(error => {
        if (this.currentGeneration === generation) {
          request.state = {
            ...request.state,
            error: error instanceof Error ? error : new Error(String(error)),
            lastUpdated: Date.now(),
            loading: false,
          };
          this.notifyHandlers(request);
        }
      })
      .finally(() => {
        request.queryPromise = null;
      });
  }

  /**
   * Fetches runs that were marked as incomplete in a previous query response.
   * This method is called automatically when the initial query returns incomplete runs.
   *
   * The follow-up query uses:
   * - The current runsById as prevRunsById (so server knows what we have)
   * - A new lastUpdated computed from existing runs (so we don't re-fetch unchanged runs)
   * - The server will return: updated runs + the previously incomplete runs
   */
  private async fetchIncompleteRuns({
    request,
    generation,
    loggingInfo,
    attempt = 0,
    traceId,
    isErrorRetry = false,
    previousIncomplete,
    timeoutOverride,
    heldBackForRetryAfter = false,
  }: {
    request: BucketedQueryRequest;
    generation: number;
    loggingInfo?: LoggingInfo;
    attempt: number;
    traceId: string;
    isErrorRetry: boolean;
    // Set of run IDs that were incomplete after the previous attempt.
    // Used to detect no-progress retries and eject the ladder early.
    previousIncomplete: string[];
    // When set, bypasses the normal TIMEOUT_DELAYS ladder and uses this
    // value as the request timeout. Its presence also signals that we've
    // already escalated in response to a no-progress retry, so a second
    // no-progress result should exit instead of escalating again.
    timeoutOverride?: number;
    heldBackForRetryAfter?: boolean;
  }) {
    if (attempt > INCOMPLETE_FETCH_MAX_RETRIES) {
      request.state = {
        ...request.state,
        loading: false,
      };
      this.notifyHandlers(request);
      return;
    }

    const timeout = timeoutOverride ?? getTimeoutForAttempt(attempt);

    try {
      // Use the current state's runsById - this contains all successfully loaded runs
      // The queryFn will compute lastUpdated from these runs' updatedAt timestamps
      const data = await this.fetchData({
        request,
        traceId,
        attemptNumber: attempt,
        expressions: request.expressions,
        generation,
        nBuckets: request.nBuckets,
        prevRunsById: request.state.data?.runsById ?? {},
        queryType: request.queryType,
        runsDataQuery: request.runsDataQuery,
        singleQuery: request.singleQuery,
        loggingInfo: loggingInfo ?? DEFAULT_LOGGING_INFO,
        timeout,
        cause: incompleteRefetchCause(isErrorRetry, heldBackForRetryAfter),
      });

      // Check if this request is stale
      if (this.currentGeneration !== generation) {
        return;
      }

      // Merge new runs with existing runs
      const mergedRunsById = {
        ...request.state.data?.runsById,
        ...data.runsById,
      };

      const hasIncomplete = data.incomplete && data.incomplete.length > 0;

      request.state = {
        ...request.state,
        data: {
          ...data,
          runsById: mergedRunsById,
          // Use the new order from the server (includes all runs)
          order: data.order,
        },
        lastUpdated: Date.now(),
        loading: Boolean(hasIncomplete),
      };

      this.notifyHandlers(request);

      logIncompleteAction({
        phase: 'retry',
        traceId: traceId,
        attempt: attempt,
        incompleteCount: data.incomplete?.length ?? 0,
        queryType: request.queryType,
        timeoutSeconds: timeout,
      });

      const currentIncomplete = data.incomplete ?? [];

      // If still incomplete, retry — unless this attempt returned the same
      // set of incomplete runs as the previous one. In that case the normal
      // ladder isn't helping (likely a slow/sick backend), so we skip
      // straight to a single long-timeout attempt. If that one also fails
      // to make progress, we eject and let the user render partial data.

      if (hasIncomplete) {
        const heldBack = await delayIncompleteRetry(data.retryAfterSeconds);
        if (this.currentGeneration !== generation) {
          return;
        }

        if (sameIncompleteSet(currentIncomplete, previousIncomplete)) {
          const alreadyEscalated = timeoutOverride !== undefined;

          if (alreadyEscalated) {
            // We already gave the longer timeout a shot. Exit with whatever
            // partial data we have.
            logIncompleteAction({
              phase: 'no_progress_eject',
              traceId,
              attempt,
              incompleteCount: currentIncomplete.length,
              queryType: request.queryType,
              timeoutSeconds: timeout,
            });
            request.state = {
              ...request.state,
              loading: false,
            };
            this.notifyHandlers(request);
            return;
          }

          logIncompleteAction({
            phase: 'no_progress_escalate',
            traceId,
            attempt,
            incompleteCount: currentIncomplete.length,
            queryType: request.queryType,
            timeoutSeconds: timeout,
          });
          this.fetchIncompleteRuns({
            request,
            generation,
            loggingInfo,
            attempt: attempt + 1,
            traceId,
            isErrorRetry: false,
            previousIncomplete: currentIncomplete,
            timeoutOverride: NO_PROGRESS_ESCALATION_TIMEOUT,
            heldBackForRetryAfter: heldBack,
          });
          return;
        }

        this.fetchIncompleteRuns({
          request,
          generation,
          loggingInfo,
          attempt: attempt + 1,
          traceId,
          isErrorRetry: false,
          previousIncomplete: currentIncomplete,
          heldBackForRetryAfter: heldBack,
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      // Consecutive errors = stop the sequence
      if (isErrorRetry) {
        logIncompleteAction({
          phase: 'error_retry',
          traceId,
          attempt,
          incompleteCount: request.state.data?.incomplete?.length ?? 0,
          queryType: request.queryType,
          errorMessage,
        });
        request.state = {
          ...request.state,
          loading: false,
        };
        this.notifyHandlers(request);
        return;
      }

      // First error: log and retry once
      logIncompleteAction({
        phase: 'error_retry',
        traceId,
        attempt,
        incompleteCount: request.state.data?.incomplete?.length ?? 0,
        queryType: request.queryType,
        errorMessage,
      });
      this.fetchIncompleteRuns({
        request,
        generation,
        loggingInfo,
        attempt: attempt + 1,
        traceId,
        isErrorRetry: true,
        previousIncomplete,
        timeoutOverride,
      });
    }
  }

  registerRequest({
    expressions,
    handler,
    nBuckets,
    queryType,
    runsDataQuery: panelQuery,
    singleQuery,
    loggingInfo,
  }: {
    expressions: string[] | undefined;
    handler: RequestHandler;
    nBuckets: number;
    queryType: BucketedQueryType;
    runsDataQuery: Exclude<RunsDataQueryConfig, 'queries'>;
    singleQuery: SingleQuery;
    loggingInfo: LoggingInfo;
  }) {
    const generation = this.currentGeneration;
    const runsDataQuery = withoutRunsetQueries(panelQuery);
    // queryType is part of the request identity. A change to the resolved query
    // type (e.g. the admin "Delta Query Type" override, or a ramp-flag flip)
    // keeps the same query vars but must re-issue the query. Matching only on
    // nBuckets/runsDataQuery/singleQuery would reuse the existing request and
    // never start a query with the new type — the manager would keep polling the
    // stale type. A fresh request also starts with empty prevRunsById, avoiding
    // a delta-merge across the differently-shaped backend-lines vs bucketed data.
    let request = this.requests.find(
      r =>
        r.queryType === queryType &&
        r.nBuckets === nBuckets &&
        isEqual(r.runsDataQuery, runsDataQuery) &&
        isEqual(r.singleQuery, singleQuery)
    );

    let initialState;

    if (!request) {
      const existingState = this.findBestExistingData(
        nBuckets,
        runsDataQuery,
        singleQuery
      );
      // Borrow the displayed brackets as well as the fetched points. A second
      // axis-bound edit may keep one bound unchanged and still need its bracket.
      const lowerFidelityState = existingState
        ? bracketHistoriesData(
            existingState,
            runsDataQuery.bucketedHistorySpecs?.[0]?.xAxis ?? '_step'
          )
        : null;
      if (lowerFidelityState) {
        initialState = {...lowerFidelityState, loading: true};
      }

      const newRequest: BucketedQueryRequest = {
        expressions,
        handlers: [],
        nBuckets,
        runningAttempts: new Set(),
        queryPromise: null,
        queryType,
        runsDataQuery,
        singleQuery,
        state: {
          data: null,
          error: null,
          lastUpdated: Date.now(),
          loading: true,
          // We put the lower fidelity data on the state so that downstream we can pop off boundary points if we need to improve chart rendering. See: `bracketHistoryData`
          lowerFidelityData: lowerFidelityState?.data ?? null,
        },
      };
      this.requests.push(newRequest);

      this.startQuery(newRequest, generation, loggingInfo);
      request = newRequest;
    }
    if (!initialState) {
      initialState = request.state;
    }

    request.handlers.push({handler, active: true});
    handler(initialState);

    return handler;
  }

  unregisterRequest(handler: RequestHandler) {
    for (const request of this.requests) {
      const handlerObj = request.handlers.find(h => h.handler === handler);
      if (handlerObj) {
        handlerObj.active = false;
        if (
          request.runningAttempts.size > 0 &&
          !request.handlers.some(h => h.active)
        ) {
          // The last panel left while a call is running. Wait one microtask
          // before recording that: React runs every effect cleanup and then
          // every effect setup in one pass, so a panel that registers again
          // (StrictMode replay, or an effect re-run with the same query) does
          // it before the microtask runs, and we don't count it as leaving.
          const panelLeftTime = performance.now();
          queueMicrotask(() => {
            if (request.handlers.some(h => h.active)) {
              return;
            }
            // `??=` keeps the first time if a panel comes back and leaves again.
            for (const attempt of request.runningAttempts) {
              attempt.panelLeftTime ??= panelLeftTime;
            }
          });
        }
        return;
      }
    }
    throw new Error('Handler not found when trying to unregister request');
  }

  poll() {
    const now = Date.now();
    const POLL_THRESHOLD = 10000;
    const generation = this.currentGeneration;

    this.cleanupInactiveRequests();

    this.requests.forEach(request => {
      if (
        !request.queryPromise &&
        // Currently fetchIncompleteRuns does NOT set queryPromise at all during the retry loop
        // so in order to prevent us from re-polling while a fetchIncompleteRuns is in progress,
        // we add a check here to make sure we aren't in loading state.
        //
        // WARNING: this works for now, because currently the only time where loading can be true
        // but queryPromise is null is during fetchIncompleteRuns iterations. However, if
        // that changes in the future, this check might block us from polling. The better long-term
        // fix is to make fetchIncompleteRuns set queryPromise.
        !request.state.loading &&
        now - request.state.lastUpdated > POLL_THRESHOLD
      ) {
        this.startQuery(request, generation, undefined, 'poll');
      }
    });
  }

  private async fetchData({
    request,
    traceId,
    attemptNumber,
    expressions,
    generation,
    nBuckets,
    prevRunsById,
    queryType,
    runsDataQuery,
    singleQuery,
    loggingInfo,
    timeout,
    cause,
  }: {
    request: BucketedQueryRequest;
    // Same for every call in one query chain. incomplete_fetch actions report
    // the same ID.
    traceId: string;
    // 0 for the call startQuery makes, then 1, 2, ... for its follow-ups.
    attemptNumber: number;
    expressions: string[] | undefined;
    generation: number;
    nBuckets: number;
    prevRunsById: RunsDataById;
    queryType: BucketedQueryType;
    runsDataQuery: Exclude<RunsDataQueryConfig, 'queries'>;
    singleQuery: SingleQuery;
    loggingInfo: LoggingInfo;
    timeout: number | undefined;
    cause?: RequestCause;
  }): Promise<BucketedData> {
    // Only follow-ups can start without a panel. Attempt 0 always has one:
    // registerRequest adds the panel's handler right after starting it, and
    // poll() only starts queries for requests that still have a panel.
    const panelWaiting = request.handlers.some(h => h.active);
    const attempt: QueryAttempt = {
      startTime: performance.now(),
      startedWithoutPanel: attemptNumber > 0 && !panelWaiting,
    };
    request.runningAttempts.add(attempt);

    // Report in `finally` so failed calls are counted too. The error still
    // reaches the caller.
    let result: BucketedData;
    let outcome: 'success' | 'error' = 'error';
    try {
      result = await this.queryFn({
        expressions,
        nBuckets,
        currentRunsById: prevRunsById,
        queryType,
        runsDataQuery,
        singleQuery,
        loggingInfo,
        timeout,
        cause,
      });
      outcome = 'success';
    } finally {
      const endTime = performance.now();
      request.runningAttempts.delete(attempt);
      const panelLeftWhileRunning = attempt.panelLeftTime !== undefined;
      recordRumDurationVital('workspace.delta_query_attempt', {
        // RUM wants an epoch time; attempt.startTime is from performance.now().
        startTime: performance.timeOrigin + attempt.startTime,
        duration: endTime - attempt.startTime,
        context: {
          queryType,
          traceId,
          attempt: attemptNumber,
          cause: cause ?? 'initial',
          outcome,
          cancellationCandidate:
            attempt.startedWithoutPanel || panelLeftWhileRunning,
          startedWithoutPanel: attempt.startedWithoutPanel,
          panelLeftWhileRunning,
          ...(attempt.panelLeftTime !== undefined && {
            msBeforePanelLeft: attempt.panelLeftTime - attempt.startTime,
            msAfterPanelLeft: endTime - attempt.panelLeftTime,
          }),
        },
      });
    }

    /**
     * We need a way to associate the runset ID with each run so that we can populate it correctly later.
     */
    for (const run of Object.values(result.runsById)) {
      run.runsetId = this.runsetId;
    }

    if (generation !== this.currentGeneration) {
      console.log('STALE REQUEST CANCELLED');
    }

    return result;
  }

  private notifyHandlers(request: BucketedQueryRequest) {
    request.handlers.forEach(h => {
      if (h.active) {
        h.handler(request.state);
      }
    });
  }

  private findBestExistingData(
    nBuckets: number,
    runsDataQuery: RunsDataQueryConfig,
    singleQuery: SingleQuery
  ): BucketedQueryState | null {
    // First try to find a request with same query but fewer buckets. This
    // runset's filters and selections live only in singleQuery.
    const sameScopeRequest = this.requests
      .filter(
        r =>
          r.nBuckets <= nBuckets &&
          !r.state.loading &&
          isEqual(r.runsDataQuery, runsDataQuery) &&
          isEqual(r.singleQuery, singleQuery)
      )
      .sort((a, b) => b.nBuckets - a.nBuckets)[0];

    if (sameScopeRequest) {
      return sameScopeRequest.state;
    }

    // If no exact match found, look for requests with same buckets but more zoomed out
    const zoomedOutRequests = this.requests
      .filter(
        r =>
          r.nBuckets === nBuckets &&
          !r.state.loading &&
          // check if the history specs are identical, ignoring bounds
          isEqual(
            r.runsDataQuery.bucketedHistorySpecs?.map(spec => ({
              ...spec,
              minStep: undefined,
              maxStep: undefined,
              minX: undefined,
              maxX: undefined,
            })),
            runsDataQuery.bucketedHistorySpecs?.map(spec => ({
              ...spec,
              minStep: undefined,
              maxStep: undefined,
              minX: undefined,
              maxX: undefined,
            }))
          ) &&
          isZoomContained(runsDataQuery, r.runsDataQuery)
      )
      // Sort by "tightness" - if a is tighter than b, it should come first
      .sort((a, b) =>
        isZoomContained(a.runsDataQuery, b.runsDataQuery) ? -1 : 1
      )[0];

    return zoomedOutRequests?.state ?? null;
  }
}

/**
 * Return the min or max value depending on what kind of bucketedHistorySpec it is
 * Custom x-axis specs use minX and maxX instead of minStep and maxStep
 */
function getMinMax(
  spec: BucketedHistorySpec
): [number | undefined, number | undefined] {
  if (specIsStepAxis(spec)) {
    return [spec.minStep, spec.maxStep];
  }
  return [spec.minX, spec.maxX];
}

function queryZoomRange(query: RunsDataQueryConfig): Range | null {
  if (!query.bucketedHistorySpecs || query.bucketedHistorySpecs.length === 0) {
    return null;
  }

  // Get the first spec's range
  const spec = query.bucketedHistorySpecs[0];
  if (!spec) {
    return null;
  }

  const [firstMin, firstMax] = getMinMax(spec);

  // Check if all specs have the same range (including undefined)
  const allMatch = query.bucketedHistorySpecs.every(spec => {
    const [min, max] = getMinMax(spec);
    return min === firstMin && max === firstMax;
  });

  if (!allMatch) {
    return null;
  }

  return {min: firstMin ?? null, max: firstMax ?? null};
}

/**
 * Determines if one query's zoom range is "more specific" than another.
 * Returns true if innerQuery specifies a more constrained range than outerQuery
 * in at least one dimension without being less constrained in the other.
 *
 * A finite bound is always considered more specific than null/undefined.
 *
 * Examples:
 * inner {min: 100, max: null} vs outer {min: null, max: null} -> true
 * inner {min: 100, max: 200} vs outer {min: 50, max: 300} -> true
 * inner {min: 100, max: 300} vs outer {min: 50, max: 300} -> true
 * inner {min: 50, max: 200} vs outer {min: 50, max: 300} -> true
 * inner {min: 1, max: 20} vs outer {min: null, max: 10} -> false (max is less constrained)
 */
function isZoomContained(
  innerQuery: RunsDataQueryConfig,
  outerQuery: RunsDataQueryConfig
): boolean {
  const innerRange = queryZoomRange(innerQuery);
  const outerRange = queryZoomRange(outerQuery);

  // If either query doesn't have a consistent range across its specs, they're not comparable
  if (innerRange === null || outerRange === null) {
    return false;
  }

  // Check min constraint
  const minIsMoreConstrained =
    // Either outer is null and inner is finite (more constrained)
    (outerRange.min === null && innerRange.min !== null) ||
    // Or both are null (equally constrained)
    (outerRange.min === null && innerRange.min === null) ||
    // Or both are finite and inner is higher (more constrained)
    (outerRange.min !== null &&
      innerRange.min !== null &&
      innerRange.min > outerRange.min);

  const minIsEqual =
    innerRange.min !== null &&
    outerRange.min !== null &&
    innerRange.min === outerRange.min;

  // Check max constraint
  const maxIsMoreConstrained =
    // Either outer is null and inner is finite (more constrained)
    (outerRange.max === null && innerRange.max !== null) ||
    // Or both are null (equally constrained)
    (outerRange.max === null && innerRange.max === null) ||
    // Or both are finite and inner is lower (more constrained)
    (outerRange.max !== null &&
      innerRange.max !== null &&
      innerRange.max < outerRange.max);

  const maxIsEqual =
    innerRange.max !== null &&
    outerRange.max !== null &&
    innerRange.max === outerRange.max;

  // Return true if:
  // - both bounds are more constrained, OR
  // - one bound is more constrained and the other is equal
  return (
    (minIsMoreConstrained && maxIsMoreConstrained) ||
    (minIsMoreConstrained && maxIsEqual) ||
    (minIsEqual && maxIsMoreConstrained)
  );
}
